import { useEffect, useRef, useState } from 'react';
import {
  CARD_TEXT_DETAIL,
  CASE_STUDY_SCRIPT,
  DIALOG_CARD_TEXT,
  DIALOG_TITLE,
  SECTION_TITLE,
} from './CaseStudyScript';
import clsx from 'clsx';
import Image from 'next/image';
import { CallaboDialogWithButton } from 'libs/callabo-ui/common/Dialog/CallaboDialogWithButton';
import { SectionTitle } from '../SectionTitle';
import analyticsInstance from 'libs/rtzr-commons/modules/marketing/Analytics';
import { useAttentionTracker } from 'app/[locale]/hooks/useAttentionTracker';

/** @description 한국어일 때만 노출되는 컴포넌트 */
export const CaseStudyList = (): JSX.Element => {
  const [targetIndex, setTargetIndex] = useState(0);
  const [detailTargetIndex, setDetailTargetIndex] = useState(targetIndex);
  const timerRef = useRef<number | null>(null);
  const { ref } = useAttentionTracker({ sectionName: 'main_case_study' });

  useEffect(() => {
    startProgress(targetIndex);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [targetIndex]);

  const startProgress = (index: number) => {
    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = window.setTimeout(() => {
      setTargetIndex(index < CASE_STUDY_SCRIPT.length - 1 ? index + 1 : 0);
    }, 5000);
  };

  const onClickCard = (index: number) => {
    setTargetIndex(index);
  };

  return (
    <section
      className="w-full pb-20 space-y-10 md:space-y-20 bg-blue-pale py-14 md:py-20 px-4 md:px-[60px]"
      ref={ref}
    >
      <SectionTitle title={SECTION_TITLE} />
      <article className="w-full max-w-[400px] mx-auto md:max-w-homepage h-[416px] rounded-lg md:rounded-[20px] md:flex border border-solid border-blue overflow-hidden">
        <ul className="relative w-full md:w-[280px] shrink-0 md:h-full md:p-8 bg-[url('/callabo/homepage/main/case_study_bg_mobile.png')] md:bg-[url('/callabo/homepage/main/case_study_bg.png')] bg-cover bg-no-repeat ">
          <div className="inset-0 absolute bg-primary opacity-[92%]" />
          {CASE_STUDY_SCRIPT.map((item, index) => {
            return (
              <li
                key={`case-study-${index}`}
                className={clsx(
                  'relative z-10 items-center min-h-[80px] md:min-h-16 py-6 md:px-0 md:py-2 text-white cursor-pointer text-base break-keep border-b border-solid border-white md:border-opacity-30',
                  index === targetIndex ? 'font-semibold flex' : 'hidden md:flex'
                )}
                onClick={() => onClickCard(index)}
              >
                <h3 className="w-full text-center md:text-left px-4 md:px-0">{item.title}</h3>
                <div
                  className={clsx(
                    'h-[2px] md:h-px absolute -bottom-[2px] md:-bottom-px bg-primary md:bg-white',
                    targetIndex === index ? 'animate-progress' : 'w-0'
                  )}
                />
              </li>
            );
          })}
        </ul>
        <div className="w-full h-[calc(100%-100px)] md:h-full flex flex-col gap-5 md:gap-10 bg-white p-6 md:py-10 md:px-[60px]">
          <Image src={'/callabo/homepage/main/quote.svg'} width={14} height={24} alt="quote" />
          <p
            dangerouslySetInnerHTML={{
              __html: CASE_STUDY_SCRIPT[targetIndex].contents[0].card,
            }}
            className="text-black-light text-sm md:text-base max-w-[663px] mb-auto md:mb-10 !leading-[150%]"
          />
          <CallaboDialogWithButton closable>
            {({ open }) => (
              <button
                onClick={() => {
                  setDetailTargetIndex(targetIndex);
                  open();
                }}
                className="text-primary text-sm md:text-base font-semibold"
                onMouseDown={() => {
                  analyticsInstance.logEvent('click_case_study_detail', {
                    card_number: targetIndex + 1,
                  });
                }}
              >
                {CARD_TEXT_DETAIL}
              </button>
            )}
            {({ close }) => (
              <section className="relative md:max-w-[1100px] h-[80vh] max-h-[820px] py-5 overflow-hidden">
                <button onClick={close} className="absolute top-0 right-0">
                  <Image src={'/callabo/close_dark.svg'} width={24} height={24} alt="close" />
                </button>
                <h4 className="text-xs md:text-lg text-gray-smoke text-center">{DIALOG_TITLE}</h4>
                <p
                  dangerouslySetInnerHTML={{
                    __html: CASE_STUDY_SCRIPT[detailTargetIndex].title,
                  }}
                  className="text-base md:text-[32px] font-semibold text-center mt-1.5 md:mt-5 px-2"
                />
                <div className="overflow-scroll max-h-[calc(100%-50px)] md:max-h-[calc(100%-120px)] mt-5 md:mt-14 custom-scrollbar">
                  {[...Array(4)].map((_, idx) => {
                    return (
                      <article
                        key={`case-section-${idx}`}
                        className="mt-5 flex flex-col md:flex-row md:items-center gap-5 md:gap-12 px-2 md:px-4 text-black-light"
                      >
                        <div className="flex flex-col justify-between gap-2 md:h-[410px] w-full md:min-w-[290px] md:max-w-[290px] md:max-h-[410px] bg-primary rounded-lg md:rounded-[20px] p-4 md:pt-6 md:pb-8 md:px-6">
                          <Image
                            src={'/callabo/homepage/main/quote_for_dialog.svg'}
                            width={14}
                            height={24}
                            alt="quote"
                          />
                          <p
                            dangerouslySetInnerHTML={{
                              __html: CASE_STUDY_SCRIPT[detailTargetIndex].contents[idx].card,
                            }}
                            className="text-white text-sm md:text-base"
                          />
                          <p className="text-xs md:text-sm text-white opacity-50 select-none">
                            {DIALOG_CARD_TEXT}
                          </p>
                        </div>
                        <div className="md:mt-7 md:h-[410px] pb-2 md:pb-0">
                          <span className="text-xs md:text-base font-semibold pb-2 md:pb-4 border-b border-solid border-black-light">
                            {CASE_STUDY_SCRIPT[detailTargetIndex].contents[idx].subTitle}
                          </span>
                          <p
                            dangerouslySetInnerHTML={{
                              __html:
                                CASE_STUDY_SCRIPT[detailTargetIndex].contents[idx].description,
                            }}
                            className="text-sm md:text-base whitespace-pre-line mt-5 md:mt-10"
                          />
                        </div>
                      </article>
                    );
                  })}
                </div>
              </section>
            )}
          </CallaboDialogWithButton>
        </div>
      </article>
    </section>
  );
};
